import { FC, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { CheckBox } from 'src/components/CheckBox'
import { ReactComponent as LoudspeakerIcon } from 'src/assets/svg/loudspeaker.svg'
import { ContentPerformanceMockData } from 'src/pages/ContentAnalysisDetail/mockData/contentPerformanceMockData'
import { useDisableScroll } from 'src/hooks/useDisableScroll'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import styles from './contentPerformanceFilterModal.module.scss'

interface ContentPerformanceFilterModalProps {
  data: ContentPerformanceMockData
  isOpen: boolean
  onClose: () => void
  onSave: (
    platforms: Set<string>,
    filters: Record<
      string,
      {
        less: boolean
        more: boolean
        number: string
      }
    >
  ) => void
  rangeFilters: Record<
    string,
    {
      less: boolean
      more: boolean
      number: string
    }
  >
  selectedPlatforms: Set<string>
}

export const ContentPerformanceFilterModal: FC<
  ContentPerformanceFilterModalProps
> = ({
  data,
  isOpen,
  onClose,
  onSave,
  rangeFilters: initialRangeFilters,
  selectedPlatforms: initialSelectedPlatforms,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [openSections, setOpenSections] = useState<Set<string>>(new Set())
  const [selectedPlatforms, setSelectedPlatforms] = useState<Set<string>>(
    initialSelectedPlatforms
  )

  // Range filter states for each section
  const [rangeFilters, setRangeFilters] = useState<
    Record<
      string,
      {
        less: boolean
        more: boolean
        number: string
      }
    >
  >(initialRangeFilters)

  // Update local state when props change
  useEffect(() => {
    setSelectedPlatforms(initialSelectedPlatforms)
    setRangeFilters(initialRangeFilters)
  }, [initialSelectedPlatforms, initialRangeFilters])

  const totalPosts = data.platforms.reduce((sum, p) => sum + p.posts, 0)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  useDisableScroll(isOpen)

  if (!isOpen) return null

  const toggleSection = (section: string) => {
    setOpenSections((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(section)) {
        newSet.delete(section)
      } else {
        newSet.add(section)
      }
      return newSet
    })
  }

  const togglePlatform = (platform: string) => {
    setSelectedPlatforms((prev) => {
      const newSet = new Set(prev)
      if (platform === 'all') {
        if (newSet.has('all')) {
          return new Set()
        } else {
          return new Set(['all', ...data.platforms.map((p) => p.platform)])
        }
      } else {
        if (newSet.has(platform)) {
          newSet.delete(platform)
          newSet.delete('all')
        } else {
          newSet.add(platform)
          if (newSet.size === data.platforms.length) {
            newSet.add('all')
          }
        }
      }
      return newSet
    })
  }

  const handleRangeFilterChange = (
    section: string,
    field: 'more' | 'less',
    value: boolean
  ) => {
    setRangeFilters((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }))
  }

  const handleNumberChange = (section: string, value: string) => {
    setRangeFilters((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        number: value,
      },
    }))
  }

  const renderRangeFilter = (sectionId: string) => {
    const filter = rangeFilters[sectionId] || {
      less: false,
      more: false,
      number: '',
    }

    return (
      <div className={styles.rangeFilter}>
        <div className={styles.rangeCheckboxGroup}>
          <button
            className={`${styles.rangeCheckbox} ${
              filter.more ? styles.rangeCheckboxChecked : ''
            }`}
            onClick={() =>
              handleRangeFilterChange(sectionId, 'more', !filter.more)
            }
            type="button"
          >
            <CheckBox isChecked={filter.more} onChange={() => {}} />
            <span>More</span>
          </button>

          <div className={styles.divider} />

          <button
            className={`${styles.rangeCheckbox} ${
              filter.less ? styles.rangeCheckboxChecked : ''
            }`}
            onClick={() =>
              handleRangeFilterChange(sectionId, 'less', !filter.less)
            }
            type="button"
          >
            <CheckBox isChecked={filter.less} onChange={() => {}} />
            <span>Less</span>
          </button>
        </div>

        <span className={styles.thanLabel}>Than</span>

        <input
          className={styles.numberInput}
          onChange={(e) => handleNumberChange(sectionId, e.target.value)}
          placeholder="Number"
          type="text"
          value={filter.number}
        />
      </div>
    )
  }

  const filterSections = [
    {
      id: 'platforms',
      label: 'Platforms',
      status: 'All Platforms Selected',
    },
    { id: 'posts', label: 'Posts', status: 'All Range Selected' },
    { id: 'reactions', label: 'Reactions', status: 'All Range Selected' },
    { id: 'comments', label: 'Comments', status: 'All Range Selected' },
    { id: 'shares', label: 'Shares', status: 'All Range Selected' },
    { id: 'views', label: 'Views', status: 'All Range Selected' },
    { id: 'followers', label: 'Followers', status: 'All Range Selected' },
    { id: 'err', label: 'ERR', status: 'All Range Selected' },
    { id: 'erv', label: 'ERV', status: 'All Range Selected' },
  ]

  const renderFilterContent = (sectionId: string) => {
    if (sectionId === 'platforms') {
      const allSelected = selectedPlatforms.has('all')
      return (
        <div className={styles.platformsList}>
          <div
            className={styles.platformItem}
            onClick={() => togglePlatform('all')}
          >
            <div className={styles.platformInfo}>
              <CheckBox isChecked={allSelected} isDouble />
              <span className={styles.platformName}>All Platforms</span>
            </div>
            <div className={styles.platformMeta}>
              <span className={styles.bullet}>•</span>
              <span className={styles.platformCount}>{totalPosts}</span>
              <LoudspeakerIcon className={styles.loudspeakerIcon} />
            </div>
          </div>
          {data.platforms.map((platform) => {
            const isSelected = selectedPlatforms.has(platform.platform)
            return (
              <div
                className={styles.platformItem}
                key={platform.platform}
                onClick={() => togglePlatform(platform.platform)}
              >
                <div className={`${styles.platformInfo} ${styles.subItem}`}>
                  <CheckBox isChecked={isSelected} />
                  <span className={styles.platformName}>
                    {platformDisplayLabel(platform.platform)}
                  </span>
                </div>
                <div className={styles.platformMeta}>
                  <span className={styles.bullet}>•</span>
                  <span className={styles.platformCount}>{platform.posts}</span>
                  <LoudspeakerIcon className={styles.loudspeakerIcon} />
                </div>
              </div>
            )
          })}
        </div>
      )
    }

    // For all other sections (posts, reactions, comments, etc.)
    return renderRangeFilter(sectionId)
  }

  return createPortal(
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent} ref={ref}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>Filter</h2>
          <button
            className={styles.closeButton}
            onClick={onClose}
            type="button"
          >
            <svg fill="none" height="24" viewBox="0 0 24 24" width="24">
              <path
                d="M18 6L6 18M6 6L18 18"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </button>
        </div>

        <div className={styles.modalBody}>
          {filterSections.map((section) => (
            <div className={styles.filterSection} key={section.id}>
              <button
                className={styles.filterHeader}
                onClick={() => toggleSection(section.id)}
                type="button"
              >
                <span className={styles.filterLabel}>{section.label}</span>
                <div className={styles.filterStatus}>
                  <span className={styles.statusText}>
                    <span className={styles.statusHighlight}>All</span>{' '}
                    {section.id === 'platforms'
                      ? 'Platforms Selected'
                      : 'Range Selected'}
                  </span>
                  <svg
                    className={`${styles.chevron} ${
                      openSections.has(section.id) ? styles.chevronOpen : ''
                    }`}
                    fill="none"
                    height="20"
                    viewBox="0 0 20 20"
                    width="20"
                  >
                    <path
                      d="M5 7.5L10 12.5L15 7.5"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                    />
                  </svg>
                </div>
              </button>

              {openSections.has(section.id) && (
                <div className={styles.filterContent}>
                  {renderFilterContent(section.id)}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className={styles.modalFooter}>
          <button
            className={styles.cancelButton}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className={styles.saveButton}
            onClick={() => onSave(selectedPlatforms, rangeFilters)}
            type="button"
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
